import { NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';
import { useState, useRef, useEffect } from 'react';
import {
  LayoutDashboard,
  CalendarDays,
  CalendarClock,
  Send,
  BarChart3,
  MessageSquare,
  MessageCircle,
  AudioLines,
  FileText,
  FolderKanban,
  ContactRound,
  Newspaper,
  Settings,
  CircleHelp,
  Link2,
  Plus,
  LogOut,
  MoreHorizontal,
  X,
  Globe,
  Plug,
  UserPlus,
  FolderPlus,
  Search,
  ShieldAlert,
  Images,
  PanelLeftOpen,
  PanelLeftClose,
} from 'lucide-react';
import { brand } from '@iheart/shared';
import { cn } from '@iheart/ui';
import { useAuth, useIsSuperAdmin } from '../lib/auth';
import { usePref } from '../lib/uiPrefs';
import { NotificationBell } from '../components/NotificationBell';
import { RailTooltip, computeRailPanelPos } from '../components/RailTooltip';
import { ChatWidget } from '../components/chat-widget';
import { PageHighlight } from '../components/PageHighlight';

import { WorkspaceSwitcher, WorkspaceNavLink } from '../components/WorkspaceSwitcher';
import { ProfileFlyout } from '../components/ProfileFlyout';
import {
  CommandPalette,
  useCommandPalette,
  useGlobalShortcuts,
} from '../components/CommandPalette';
import {
  KeyboardShortcutsHelp,
  useKeyboardShortcutsHelp,
} from '../components/KeyboardShortcutsHelp';
import { useConfirm } from '../components/ConfirmDialog';
import { PaywallGate } from '../components/PaywallGate';
import { MfaRequiredGate } from '../components/MfaRequiredGate';
/**
 * Persistent app shell:
 *  - Desktop ≥1024: left icon rail (5 sections) + content
 *  - Tablet/Mobile <1024: bottom tab bar
 *
 * Icons match the Canva mockups (calendar, paper plane, bar chart, speech
 * bubble, audio bars). Active item shows the brand gradient on its bg.
 */

interface NavItem {
  to: string;
  label: string;
  Icon: typeof CalendarDays;
}

// Main left-panel order (shared by desktop rail + mobile). The fixed rail
// actions (Search, Quick Add, Settings, Resources, Super Admin) follow after.
const NAV: NavItem[] = [
  { to: '/dashboard', label: 'Dashboard', Icon: LayoutDashboard },
  { to: '/publish', label: 'Content Planner', Icon: Send },
  { to: '/calendar', label: 'Content Calendar', Icon: CalendarDays },
  { to: '/settings/bio', label: 'Link in Bio', Icon: Link2 },
  { to: '/engage', label: 'Engage', Icon: MessageSquare },
  { to: '/listening', label: 'Listening', Icon: AudioLines },
  { to: '/seo', label: 'SEO & GEO', Icon: Globe },
  { to: '/media', label: 'Public Relations', Icon: Newspaper },
  { to: '/media-library', label: 'Media', Icon: Images },
  { to: '/projects', label: 'Projects', Icon: FolderKanban },
  { to: '/planner', label: 'Task Planner', Icon: CalendarClock },
  { to: '/crm', label: 'CRM', Icon: ContactRound },
  { to: '/chat', label: 'Team Chat', Icon: MessageCircle },
  { to: '/analytics', label: 'Analytics', Icon: BarChart3 },
  { to: '/reports', label: 'Reports', Icon: FileText },
];

export function AppShell() {
  const { user, logout } = useAuth();
  const isSuperAdmin = useIsSuperAdmin();
  const confirm = useConfirm();
  const location = useLocation();
  // The footer bar + chat bubbles (ChatWidget) render on every page EXCEPT the
  // full-page Team Chat route, so only there does <main> use the full height.
  const reserveBottomBar = location.pathname !== '/chat';
  // Desktop rail: collapsed icon-only (default) vs expanded with labels.
  // Synced across devices via ui_prefs (localStorage-cached for instant paint).
  const [railExpanded, setRailExpanded] = usePref('railExpanded', false);
  const [showProfile, setShowProfile] = useState(false);
  const { open: cmdOpen, setOpen: setCmdOpen } = useCommandPalette();
  const { open: shortcutsOpen, setOpen: setShortcutsOpen } = useKeyboardShortcutsHelp();
  useGlobalShortcuts();
  const initials = user
    ? user.name
        .split(' ')
        .map((p) => p[0])
        .filter(Boolean)
        .slice(0, 2)
        .join('')
        .toUpperCase()
    : '?';

  return (
    <div className="relative flex h-full w-full overflow-x-hidden bg-white">
      <PaywallGate />
      <MfaRequiredGate />
      {/* Desktop / tablet icon rail.
          The rail stays overflow-visible so the workspace / notification /
          quick-add dropdowns can escape its 64px width. Only the MIDDLE feature
          nav scrolls (see the <nav> below) — the logo + Dashboard + workspace
          are pinned at the top and the utility group is pinned at the bottom, so
          on short screens every item is reachable by scrolling the middle. */}
      <aside
        className={cn(
          'fixed left-0 top-0 z-40 hidden h-screen flex-col overflow-visible border-r border-neutral-200 bg-white py-4 transition-[width] duration-200 md:flex',
          railExpanded ? 'w-60 items-stretch px-2' : 'w-16 items-center',
        )}
      >
        {/* Logo (+ wordmark when expanded) */}
        <div className={cn('mb-2 flex shrink-0 items-center', railExpanded ? 'gap-2 px-1' : 'justify-center')}>
          <img src="/logo.png" alt="iHeart CNTRL" className="size-9 shrink-0 object-contain" />
          {railExpanded && <span className="truncate text-sm font-bold text-neutral-800">iHeart CNTRL</span>}
        </div>
        {/* Expand / collapse the menu (desktop only) */}
        <button
          type="button"
          onClick={() => setRailExpanded((v) => !v)}
          aria-label={railExpanded ? 'Collapse menu' : 'Expand menu'}
          className={cn(
            'mb-2 flex shrink-0 items-center rounded-lg text-neutral-400 transition-colors hover:bg-muted hover:text-body',
            railExpanded ? 'gap-3 px-3 py-2' : 'size-10 justify-center self-center',
          )}
        >
          {railExpanded ? <PanelLeftClose className="size-5 shrink-0" /> : <PanelLeftOpen className="size-5" />}
          {railExpanded && <span className="truncate text-xs font-medium">Collapse menu</span>}
        </button>
        {/* The nav items scroll between the pinned logo (top) and the pinned
            profile/sign-out group (bottom). min-h-0 lets this flex child shrink
            below its content so the overflow actually scrolls; overscroll-contain
            stops the scroll from chaining to the page. A thin scrollbar is shown
            as a visible affordance so it's obvious the menu scrolls. Every rail
            dropdown (workspace / quick-add / notifications) and tooltip renders
            in a portal, so this overflow can't clip them. */}
        <nav
          className={cn(
            'rail-scroll flex w-full min-h-0 flex-1 flex-col gap-1 overflow-y-auto overflow-x-hidden overscroll-contain',
            railExpanded ? 'items-stretch' : 'items-center',
          )}
        >
          <RailLink item={NAV[0]!} expanded={railExpanded} />
          {NAV.slice(1).map((item) => (
            <RailLink key={item.to} item={item} expanded={railExpanded} />
          ))}
          <div className={cn('mt-auto flex shrink-0 flex-col gap-1 pt-2', railExpanded ? 'items-stretch' : 'items-center')}>
            <RailButton label="Search (⌘K)" onClick={() => setCmdOpen(true)} expanded={railExpanded}>
              <Search className="size-5" />
            </RailButton>
            <QuickAddMenu expanded={railExpanded} />
            <RailLink item={{ to: '/settings', label: 'Settings', Icon: Settings }} expanded={railExpanded} />
            <RailLink item={{ to: '/resources', label: 'Resources & Help', Icon: CircleHelp }} expanded={railExpanded} />
            {isSuperAdmin &&
              (railExpanded ? (
                <NavLink
                  to="/super-admin"
                  className={({ isActive }) =>
                    cn(
                      'flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-white transition-opacity',
                      isActive ? 'ring-2 ring-pink-300' : 'hover:opacity-90',
                    )
                  }
                  style={{ backgroundImage: brand.gradient.primary }}
                >
                  <ShieldAlert className="size-5 shrink-0" /> <span className="truncate">Super Admin</span>
                </NavLink>
              ) : (
                <RailTooltip label="Super Admin">
                  <NavLink
                    to="/super-admin"
                    className={({ isActive }) =>
                      cn(
                        'flex size-10 items-center justify-center rounded-lg text-white transition-colors',
                        isActive ? 'ring-2 ring-pink-300 ring-offset-2' : 'hover:opacity-90',
                      )
                    }
                    style={{ backgroundImage: brand.gradient.primary }}
                  >
                    <ShieldAlert className="size-5" />
                  </NavLink>
                </RailTooltip>
              ))}
          </div>
        </nav>
        {/* Pinned bottom: the active workspace, notifications, profile + sign out
            stay put while the menu scrolls, so the workspace you're in and any new
            notification are never lost off-screen. */}
        <div className={cn('flex shrink-0 flex-col gap-2 border-t border-neutral-200 pt-3', railExpanded ? 'items-start px-1' : 'items-center')}>
          <WorkspaceNavLink />
          <NotificationBell />
          {railExpanded ? (
            <button
              type="button"
              onClick={() => setShowProfile(true)}
              aria-label={`Profile: ${user?.name ?? 'User'}`}
              className="flex w-full items-center gap-2 rounded-lg px-1 py-1 text-left transition-colors hover:bg-muted"
            >
              <span
                className="flex size-9 shrink-0 items-center justify-center overflow-hidden rounded-full text-[11px] font-semibold text-white"
                style={user?.avatarUrl ? undefined : { backgroundImage: brand.gradient.primary }}
              >
                {user?.avatarUrl ? <img src={user.avatarUrl} alt="" className="size-full object-cover" /> : initials}
              </span>
              <span className="truncate text-sm font-medium text-neutral-700">{user?.name ?? 'You'}</span>
            </button>
          ) : (
            <RailTooltip label={user?.name ?? 'You'}>
              <button
                type="button"
                onClick={() => setShowProfile(true)}
                aria-label={`Profile: ${user?.name ?? 'User'}`}
                className="flex size-9 items-center justify-center overflow-hidden rounded-full text-[11px] font-semibold text-white transition-transform hover:scale-105"
                style={user?.avatarUrl ? undefined : { backgroundImage: brand.gradient.primary }}
              >
                {user?.avatarUrl ? <img src={user.avatarUrl} alt="" className="size-full object-cover" /> : initials}
              </button>
            </RailTooltip>
          )}
          <RailButton label="Sign out" expanded={railExpanded} onClick={async () => {
            const ok = await confirm({
              title: 'Are you sure you want to log out?',
              message: 'You will need to sign in again to access your workspaces.',
              confirmLabel: 'Yes, log out',
              cancelLabel: 'No, stay',
              variant: 'logout',
            });
            if (ok) void logout();
          }}>
            <LogOut className="size-5" />
          </RailButton>
        </div>
      </aside>

      {/* Profile flyout */}
      {showProfile && <ProfileFlyout onClose={() => setShowProfile(false)} />}

      {/* Command palette (Cmd+K) */}
      <CommandPalette open={cmdOpen} onClose={() => setCmdOpen(false)} />

      {/* Keyboard shortcuts help (?) */}
      <KeyboardShortcutsHelp open={shortcutsOpen} onClose={() => setShortcutsOpen(false)} />

      {/* Mobile top bar — account & context (workspace, notifications, profile,
          sign out). Fixed; the desktop rail holds these on ≥md. */}
      <header className="fixed inset-x-0 top-0 z-30 flex h-14 items-center justify-between gap-2 border-b border-neutral-200 bg-white/95 px-3 backdrop-blur md:hidden">
        {/* Workspace switcher — switch workspaces without leaving your profile. */}
        <div className="min-w-0 max-w-[62%] flex-1">
          <WorkspaceSwitcher variant="mobile" />
        </div>
        <div className="flex items-center gap-0.5">
          <NotificationBell />
          <button
            type="button"
            onClick={() => setShowProfile(true)}
            aria-label={`Profile: ${user?.name ?? 'User'}`}
            className="flex size-9 items-center justify-center overflow-hidden rounded-full text-[11px] font-semibold text-white transition-transform active:scale-95"
            style={user?.avatarUrl ? undefined : { backgroundImage: brand.gradient.primary }}
          >
            {user?.avatarUrl ? (
              <img src={user.avatarUrl} alt="" className="size-full object-cover" />
            ) : (
              initials
            )}
          </button>
          <button
            type="button"
            aria-label="Sign out"
            onClick={async () => {
              const ok = await confirm({
                title: 'Are you sure you want to log out?',
                message: 'You will need to sign in again to access your workspaces.',
                confirmLabel: 'Yes, log out',
                cancelLabel: 'No, stay',
                variant: 'logout',
              });
              if (ok) void logout();
            }}
            className="flex size-9 items-center justify-center rounded-lg text-neutral-500 hover:bg-muted hover:text-body"
          >
            <LogOut className="size-5" />
          </button>
        </div>
      </header>

      {/* Content — pt-14 clears the mobile top bar, pb-16 the bottom tab bar. */}
      {/*
        The pale pink→white→indigo brand gradient is the <main> background on
        every breakpoint, so it sits behind ALL page content (cards, links, etc.)
        — a background layer, never an overlay.

        Tablet / laptop / desktop (md+): <main> is a bounded scroll container
        whose height LEAVES ROOM for the fixed footer bar (3.5rem) AND the chat-
        bubble row that docks just above it (~4rem) — reserved via height, not
        padding, because pages wrap their content in `min-h-full`, which fills the
        padding box and would otherwise slide under the bubbles. Content uses the
        full width — no right strip. The full-page Team Chat route hides the
        footer/bubbles, so there it uses the taller (100vh − 3.5rem) height.

        Mobile: content uses the full width; the bubbles move to a centred row
        above the bottom tab bar (see ChatBubble), so extra bottom padding (pb-32)
        keeps content clear of that row + the tab bar.
      */}
      <main id="main-content" className={cn('main-scroll relative flex min-h-0 min-w-0 flex-1 flex-col overflow-y-auto overflow-x-hidden bg-gradient-to-br from-pink-50 via-white to-indigo-50 pb-[calc(var(--mob-bottom,7rem)+4.5rem)] pt-14 transition-[margin] duration-200 md:pr-0 md:pt-0 md:pb-2', railExpanded ? 'md:ml-60' : 'md:ml-16', reserveBottomBar ? 'md:h-[calc(100vh-7.5rem)]' : 'md:h-[calc(100vh-3.5rem)]')}>
        <Outlet />
      </main>

      {/* Quick actions bar removed — quick links now inline per page */}

      {/* Mobile bottom tabs */}
      <MobileTabBar />

      {/* Floating chat widget */}
      <ChatWidget />

      {/* Domina's "Show me" page-flash highlight */}
      <PageHighlight />
    </div>
  );
}

function RailLink({ item, expanded }: { item: NavItem; expanded?: boolean }) {
  const link = (
    <NavLink to={item.to} end={item.to === '/dashboard'} className="block">
      {({ isActive }) => (
        <motion.span
          whileHover={{ scale: expanded ? 1 : brand.motion.grow }}
          whileTap={{ scale: expanded ? 0.99 : brand.motion.grow }}
          transition={brand.motion.spring}
          className={cn(
            'flex items-center rounded-lg text-neutral-500 transition-colors hover:bg-muted hover:text-body',
            expanded ? 'w-full gap-3 px-3 py-2' : 'size-10 justify-center',
            isActive && 'text-white hover:text-white',
          )}
          style={isActive ? { backgroundImage: brand.gradient.primary } : undefined}
        >
          <item.Icon className="size-5 shrink-0" />
          {expanded && <span className="truncate text-sm font-medium">{item.label}</span>}
        </motion.span>
      )}
    </NavLink>
  );
  return expanded ? link : <RailTooltip label={item.label}>{link}</RailTooltip>;
}

function RailButton({
  label,
  onClick,
  expanded,
  children,
}: {
  label: string;
  onClick?: () => void;
  expanded?: boolean;
  children: React.ReactNode;
}) {
  const btn = (
    <motion.button
      type="button"
      onClick={onClick}
      aria-label={label}
      whileHover={{ scale: expanded ? 1 : brand.motion.grow }}
      whileTap={{ scale: expanded ? 0.99 : brand.motion.grow }}
      transition={brand.motion.spring}
      className={cn(
        'flex items-center rounded-lg text-neutral-500 transition-colors hover:bg-muted hover:text-body',
        expanded ? 'w-full gap-3 px-3 py-2' : 'size-10 justify-center',
      )}
    >
      {children}
      {expanded && <span className="truncate text-sm font-medium">{label}</span>}
    </motion.button>
  );
  return expanded ? btn : <RailTooltip label={label}>{btn}</RailTooltip>;
}

/**
 * Quick Add flyout: the panel is portaled to <body> with fixed positioning so
 * it escapes the rail's scroll overflow and never clips against the 64px rail.
 */
function QuickAddMenu({ expanded }: { expanded?: boolean }) {
  const [open, setOpen] = useState(false);
  const btnRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [coords, setCoords] = useState<{ left: number; top?: number; bottom?: number; maxHeight: number }>({
    left: 0,
    maxHeight: 400,
  });
  const navigate = useNavigate();

  useEffect(() => {
    function handler(e: MouseEvent) {
      const t = e.target as Node;
      if (btnRef.current?.contains(t) || panelRef.current?.contains(t)) return;
      setOpen(false);
    }
    if (open) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  function toggle() {
    if (!open) {
      const r = btnRef.current?.getBoundingClientRect();
      // Fixed + portaled, height-capped to the available space so the menu always
      // fits the viewport on any screen size (scrolls internally if too tall).
      if (r) setCoords(computeRailPanelPos(r));
    }
    setOpen((v) => !v);
  }

  const items = [
    { label: 'Connect social account', icon: Plug, to: '/settings/integrations' },
    { label: 'Add team member', icon: UserPlus, to: '/settings/team' },
    { label: 'New workspace (client)', icon: FolderPlus, to: '/settings/workspace' },
    { label: 'New project', icon: FolderKanban, to: '/projects?new=true' },
    { label: 'New CRM contact', icon: ContactRound, to: '/crm/contacts?new=true' },
    { label: 'Create post', icon: Send, to: '/calendar?compose=1' },
    { label: 'New report template', icon: FileText, to: '/reports' },
    { label: 'Link in Bio page', icon: Globe, to: '/settings/bio' },
  ];

  const trigger = (
    <motion.button
      ref={btnRef}
      type="button"
      onClick={toggle}
      aria-label="Quick add"
      aria-expanded={open}
      whileHover={{ scale: expanded ? 1 : brand.motion.grow }}
      whileTap={{ scale: expanded ? 0.99 : brand.motion.grow }}
      transition={brand.motion.spring}
      className={cn(
        'flex items-center rounded-lg transition-colors',
        expanded ? 'w-full gap-3 px-3 py-2' : 'size-10 justify-center',
        open ? 'bg-gradient-to-br from-pink-500 to-indigo-500 text-white' : 'text-neutral-500 hover:bg-muted hover:text-body',
      )}
    >
      <Plus className="size-5 shrink-0" />
      {expanded && <span className="truncate text-sm font-medium">Quick add</span>}
    </motion.button>
  );

  return (
    <>
      {expanded ? trigger : <RailTooltip label="Quick add">{trigger}</RailTooltip>}
      {open &&
        createPortal(
          <div
            ref={panelRef}
            style={{
              position: 'fixed',
              left: coords.left,
              top: coords.top,
              bottom: coords.bottom,
              maxHeight: coords.maxHeight,
            }}
            className="z-[70] flex w-56 flex-col overflow-y-auto rounded-xl border border-neutral-200 bg-white py-1.5 shadow-lg"
          >
            <div className="shrink-0 px-3 py-1.5 text-[10px] font-medium uppercase tracking-wider text-neutral-400">
              Quick add
            </div>
            {items.map((item) => (
              <button
                key={item.to}
                type="button"
                onClick={() => {
                  setOpen(false);
                  navigate(item.to);
                }}
                className="flex w-full items-center gap-2.5 px-3 py-2 text-sm text-neutral-700 transition-colors hover:bg-neutral-50"
              >
                <item.icon className="size-4 text-neutral-400" />
                {item.label}
              </button>
            ))}
          </div>,
          document.body,
        )}
    </>
  );
}

// Mobile: show dashboard + first 4 main nav items (5 total fits the tab bar)
// Bottom tab bar = first 5 of the shared order; the rest live in the "More"
// sheet. Both derive from NAV so mobile matches the desktop rail order exactly.
const MOBILE_PRIMARY: NavItem[] = NAV.slice(0, 5); // dashboard, content planner, content calendar, link in bio, engage

const MOBILE_MORE: Array<{ to: string; label: string; Icon: typeof CalendarDays }> = [
  ...NAV.slice(5),
  { to: '/settings', label: 'Settings', Icon: Settings },
  { to: '/resources', label: 'Resources', Icon: CircleHelp },
];

function MobileTabBar() {
  const [showMore, setShowMore] = useState(false);
  const isSA = useIsSuperAdmin();

  // The whole bottom stack (menu + pinned footer) can change height — the nav
  // labels wrap to two lines on narrow phones. Measure it and publish the height
  // as --mob-bottom so the chat strip, chat buttons and page padding all sit
  // correctly above it instead of relying on a hard-coded guess.
  const stackRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = stackRef.current;
    if (!el) return;
    const apply = () => document.documentElement.style.setProperty('--mob-bottom', `${el.offsetHeight}px`);
    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(el);
    window.addEventListener('resize', apply);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', apply);
      document.documentElement.style.removeProperty('--mob-bottom');
    };
  }, []);

  return (
    <>
      {/* More overlay — z-50 so it sits ABOVE the floating chat bubbles (z-40)
          and their bar; the sheet also clears the tab bar + bubble bar. */}
      {showMore && (
        <div className="fixed inset-0 z-50 md:hidden">
          <div className="absolute inset-0 bg-black/30" onClick={() => setShowMore(false)} />
          <div className="absolute inset-x-0 bottom-[calc(var(--mob-bottom,7rem)+4.5rem)] mx-3 max-h-[70vh] overflow-y-auto rounded-xl border border-neutral-200 bg-white p-2 shadow-lg">
            <div className="mb-1 flex items-center justify-between px-2 py-1">
              <span className="text-xs font-medium text-neutral-500">More</span>
              <button
                type="button"
                onClick={() => setShowMore(false)}
                className="rounded p-1 text-neutral-400 hover:bg-muted"
              >
                <X className="size-4" />
              </button>
            </div>
            {isSA && (
              <NavLink
                to="/super-admin"
                onClick={() => setShowMore(false)}
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm',
                    isActive ? 'bg-pink-50 font-medium text-pink-600' : 'text-neutral-600 hover:bg-muted',
                  )
                }
              >
                <ShieldAlert className="size-5" />
                Super Admin
              </NavLink>
            )}
            {MOBILE_MORE.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                onClick={() => setShowMore(false)}
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm',
                    isActive ? 'bg-pink-50 font-medium text-pink-600' : 'text-neutral-600 hover:bg-muted',
                  )
                }
              >
                <item.Icon className="size-5" />
                {item.label}
              </NavLink>
            ))}
          </div>
        </div>
      )}

      {/* Bottom stack (mobile): main menu on top, the Terms/license footer
          pinned at the very bottom. Measured via stackRef so the chat strip and
          buttons sit directly above it regardless of how tall the menu is. */}
      <div ref={stackRef} className="fixed inset-x-0 bottom-0 z-30 flex flex-col md:hidden">
        {/* Main menu */}
        <nav className="flex items-stretch border-t border-neutral-200 bg-white">
          {MOBILE_PRIMARY.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/dashboard'}
              className={({ isActive }) =>
                cn(
                  'flex flex-1 flex-col items-center justify-center gap-1 py-2 text-center text-[11px] text-neutral-500',
                  isActive && 'text-pink-600',
                )
              }
            >
              {({ isActive }) => (
                <>
                  <item.Icon className={cn('size-5', isActive && 'text-pink-600')} />
                  {item.label}
                </>
              )}
            </NavLink>
          ))}
          <button
            type="button"
            onClick={() => setShowMore((v) => !v)}
            className={cn(
              'flex flex-1 flex-col items-center justify-center gap-1 py-2 text-center text-[11px] text-neutral-500',
              showMore && 'text-pink-600',
            )}
          >
            <MoreHorizontal className={cn('size-5', showMore && 'text-pink-600')} />
            More
          </button>
        </nav>
        {/* Terms / license footer pinned at the very bottom (pb clears the iPhone home indicator) */}
        <div className="flex flex-wrap items-center justify-center gap-x-2.5 gap-y-0.5 border-t border-neutral-200 bg-white px-3 py-1.5 pb-[calc(0.375rem+env(safe-area-inset-bottom))] text-center text-[10px] leading-tight text-neutral-400">
          <span>© {new Date().getFullYear()} iHeart CNTRL — a subdivision of{' '}
            <a href="https://iheartdigital.co.za" target="_blank" rel="noopener noreferrer" className="font-medium text-pink-500">iHeart Digital</a>
          </span>
          <a href="/terms-and-conditions/" className="hover:text-neutral-600">Terms</a>
          <a href="/privacy-policy/" className="hover:text-neutral-600">Privacy</a>
          <a href="/returns-and-refunds/" className="hover:text-neutral-600">Refunds</a>
          {isSA && <a href="/admin/tokens" className="hover:text-neutral-600">Admin</a>}
          {isSA && <a href="/admin/licenses" className="hover:text-neutral-600">Licenses</a>}
        </div>
      </div>
    </>
  );
}
